import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useCallback, useEffect, useMemo } from 'react'
import { useInterval } from 'react-use'
import { useTaskType } from '..'
import { off, on } from '../../../lib/events'
import { Task as TaskType } from '../../../types'
import { useRedirectWithToast } from '../../misc/use-redirect-with-toast'
import { useFetchTask } from '../use-fetch-task'

interface UseTask {
  task: TaskType
  error?: Error
  loading: boolean
  refetchTask(): void
  taskType: string | null
}

interface UseTaskParams {
  taskId: string
}

export const useTask = ({ taskId }: UseTaskParams): UseTask => {
  const { i18n } = useLingui()
  const { task, error, loading, mutate: refetchTask } = useFetchTask(taskId)
  const taskType = useTaskType(task?.category)
  const { redirectWithToast } = useRedirectWithToast()

  useEffect(() => {
    if (error) {
      redirectWithToast({
        path: '/library/',
        toastConfig: {
          closable: true,
          type: 'warning',
          description: i18n._(t`system.error.description`),
          icon: null
        }
      })
    }
  }, [i18n, redirectWithToast, error, taskType])

  const revalidateTask = useCallback(() => refetchTask(), [refetchTask])

  useEffect(() => {
    on('revalidate:tasks', revalidateTask)

    return () => {
      off('revalidate:tasks', revalidateTask)
    }
  }, [revalidateTask])

  const hasProcessingOperations = useMemo(() => {
    if (!task) return false

    const operationsToCheck = [
      'beatschords',
      'lyrics',
      'segmentation',
      'separate'
    ] as const

    return operationsToCheck.some((operation) => {
      if (task?.summary?.v1[operation]) {
        return (
          task?.summary?.v1[operation]?.status !== 'COMPLETED' &&
          task?.summary?.v1[operation]?.status !== 'FAILED'
        )
      }
      return false
    })
  }, [task])

  useInterval(
    () => {
      refetchTask()
    },
    hasProcessingOperations ? 5000 : null
  )

  return {
    refetchTask,
    task: task as TaskType,
    error,
    loading,
    taskType
  }
}
